import React from 'react';
import { TagTypes } from '../../../Api/Types/tagTypes';
import { bankDocumentsApi } from '../Api/bankDocumentsApi';
import { normalizePositiveInteger } from './lookupPagination';
import {
  mapReservationToBankDocumentLookup,
  type BankDocumentReservationLookup,
  type BankDocumentReservationLookupSource,
} from './reservationsLookupMapper';

interface BankDocumentReservationLookupArgs {
  searchTerm?: string;
  pageNumber?: number;
  pageSize?: number;
  companyId?: number | null;
}

interface BankDocumentLookupRequest {
  searchTerm?: string;
  pageNumber?: number;
  pageSize?: number;
  take?: number;
  companyId?: number | null;
}

interface BankDocumentReservationsPagedResponse {
  items?: BankDocumentReservationLookupSource[] | null;
  totalCount?: number;
  pageNumber?: number;
  pageSize?: number;
}

export const buildBankDocumentReservationsLookupUrl = (
  request: BankDocumentLookupRequest
): string => {
  const queryParams = new URLSearchParams();
  const normalizedSearchTerm = request.searchTerm?.trim();
  const normalizedPageNumber = normalizePositiveInteger(request.pageNumber, 1);
  const normalizedPageSize = normalizePositiveInteger(request.pageSize, 10);

  if (normalizedSearchTerm) {
    queryParams.set('search', normalizedSearchTerm);
  }

  if (request.companyId !== null && request.companyId !== undefined) {
    queryParams.set('companyId', request.companyId.toString());
  }

  queryParams.set(
    'pageNumber',
    normalizedPageNumber.toString()
  );
  queryParams.set('pageSize', normalizedPageSize.toString());
  queryParams.set('take', normalizedPageSize.toString());

  return `/bank-documents/lookup/reservations?${queryParams.toString()}`;
};

const bankDocumentReservationsLookupApi = bankDocumentsApi.injectEndpoints({
  endpoints: (builder) => ({
    getBankDocumentReservationsLookupRaw: builder.query<
      BankDocumentReservationsPagedResponse,
      BankDocumentLookupRequest
    >({
      query: buildBankDocumentReservationsLookupUrl,
      providesTags: [{ type: TagTypes.BankDocument, id: TagTypes.List }],
    }),
  }),
});

const { useGetBankDocumentReservationsLookupRawQuery } = bankDocumentReservationsLookupApi;

type ReservationsLookupQueryOptions = Parameters<
  typeof useGetBankDocumentReservationsLookupRawQuery
>[1];
type ReservationsLookupQueryResult = ReturnType<typeof useGetBankDocumentReservationsLookupRawQuery>;
type BankDocumentReservationsLookupQueryResult = Pick<
  ReservationsLookupQueryResult,
  'isLoading' | 'isFetching' | 'isError' | 'error' | 'refetch'
> & {
  data: BankDocumentReservationLookup[];
  pagination: {
    totalCount: number;
    pageNumber: number;
    pageSize: number;
  };
};

export const useGetBankDocumentReservationsLookupQuery = (
  args: BankDocumentReservationLookupArgs,
  options?: ReservationsLookupQueryOptions
): BankDocumentReservationsLookupQueryResult => {
  const queryOptions: ReservationsLookupQueryOptions = options;
  const normalizedSearchTerm = args.searchTerm?.trim() || undefined;
  const normalizedPageNumber = normalizePositiveInteger(args.pageNumber, 1);
  const normalizedPageSize = normalizePositiveInteger(args.pageSize, 10);

  const queryResult = useGetBankDocumentReservationsLookupRawQuery(
    {
      searchTerm: normalizedSearchTerm,
      pageNumber: normalizedPageNumber,
      pageSize: normalizedPageSize,
      take: normalizedPageSize,
      companyId: args.companyId ?? null,
    },
    queryOptions
  );

  const pagedResponse = queryResult.data;

  const data = React.useMemo(
    () =>
      (pagedResponse?.items ?? [])
        .map((reservation: BankDocumentReservationLookupSource) =>
          mapReservationToBankDocumentLookup(reservation)
        )
        .filter(
          (
            item: BankDocumentReservationLookup | undefined
          ): item is BankDocumentReservationLookup => Boolean(item)
        ),
    [pagedResponse]
  );

  return {
    data,
    isLoading: queryResult.isLoading,
    isFetching: queryResult.isFetching,
    isError: queryResult.isError,
    error: queryResult.error,
    refetch: queryResult.refetch,
    pagination: {
      totalCount: pagedResponse?.totalCount ?? 0,
      pageNumber: pagedResponse?.pageNumber ?? normalizedPageNumber,
      pageSize: pagedResponse?.pageSize ?? normalizedPageSize,
    },
  };
};
